import {
  FiType,
  FiHash,
  FiList,
  FiImage,
  FiVideo,
  FiMousePointer,
  FiMinus,
  FiArrowDown,
  FiAlertTriangle,
  FiFileText,
  FiMessageSquare,
  FiColumns,
} from "react-icons/fi";

// ─── Utilities ───────────────────────────────────────────────────────────────

/** Convert absolute media URLs to relative for local display */
export function displayUrl(url: string): string {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    if (parsed.pathname.includes("/api/media/")) return parsed.pathname;
  } catch { /* not a full URL, use as-is */ }
  return url;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type BlockType = "text" | "heading" | "image" | "button" | "divider" | "list" | "spacer" | "quote" | "callout" | "video" | "signature" | "numbered_list" | "two_column";

export type ContentBlock = {
  id: string;
  type: BlockType;
  content: string;       // Main text / markdown
  url?: string;          // For image src, button href, or video URL
  alt?: string;          // Image alt text, or callout style
  level?: 1 | 2 | 3;    // Heading level
  content2?: string;     // Second column for two_column
};

// ─── Constants ───────────────────────────────────────────────────────────────

export const EMAIL_BRAND = {
  primary: "#00263e",
  cream: "#f5f1eb",
  accent: "#1a6fa3",
  text: "#333333",
  textLight: "#666666",
  border: "#e5e1db",
  white: "#ffffff",
  logoUrl: "https://pathfinancial.com.au/logo/PATH_FinanceSolutions_LogoLong_WithWhitespace.png",
  siteUrl: "https://pathfinancial.com.au",
  companyName: "Path Financial Solutions",
  acl: "389087",
  abn: "97 193 515 627",
  address: "PO Box 4461, Springfield Central QLD 4300",
};

export const BLOCK_CONFIG: Record<BlockType, { label: string; icon: React.ComponentType<{ className?: string }>; description: string; category: string }> = {
  // Content
  text: { label: "Text", icon: FiType, description: "Paragraph of text", category: "Content" },
  heading: { label: "Heading", icon: FiHash, description: "Section heading", category: "Content" },
  list: { label: "Bullet List", icon: FiList, description: "Bullet point list", category: "Content" },
  numbered_list: { label: "Numbered List", icon: FiList, description: "Numbered step list", category: "Content" },
  quote: { label: "Quote", icon: FiMessageSquare, description: "Testimonial or quote", category: "Content" },
  two_column: { label: "Two Columns", icon: FiColumns, description: "Side-by-side content", category: "Content" },
  // Media & Actions
  image: { label: "Image", icon: FiImage, description: "Image or GIF", category: "Media" },
  video: { label: "Video", icon: FiVideo, description: "Video thumbnail link", category: "Media" },
  button: { label: "Button", icon: FiMousePointer, description: "Call-to-action button", category: "Media" },
  // Layout
  divider: { label: "Divider", icon: FiMinus, description: "Horizontal line", category: "Layout" },
  spacer: { label: "Spacer", icon: FiArrowDown, description: "Extra spacing", category: "Layout" },
  callout: { label: "Callout Box", icon: FiAlertTriangle, description: "Highlighted info box", category: "Layout" },
  signature: { label: "Signature", icon: FiFileText, description: "Email sign-off", category: "Layout" },
};

// ─── Block helpers (internal) ────────────────────────────────────────────────

// Polyfill for findLastIndex (ES2023) since our target may not support it
function findLastIdx<T>(arr: T[], predicate: (item: T) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (predicate(arr[i])) return i;
  }
  return -1;
}

let blockIdCounter = 0;
function newBlockId(): string {
  return `block-${Date.now()}-${++blockIdCounter}`;
}

// ─── Block factory ───────────────────────────────────────────────────────────

export function createBlock(type: BlockType, overrides?: Partial<ContentBlock>): ContentBlock {
  const base: ContentBlock = { id: newBlockId(), type, content: "" };
  if (type === "heading") base.level = 2;
  if (type === "divider") base.content = "---";
  if (type === "spacer") base.content = "";
  if (type === "button") { base.content = "Learn More"; base.url = "https://pathfinancial.com.au"; }
  if (type === "quote") { base.content = ""; base.alt = ""; } // alt = attribution
  if (type === "callout") { base.content = ""; base.alt = "info"; } // alt = style: info, warning, tip
  if (type === "video") { base.content = "Watch Now"; base.url = ""; base.alt = ""; } // alt = thumbnail URL
  if (type === "signature") { base.content = "Warm regards,"; }
  if (type === "numbered_list") base.content = "";
  if (type === "two_column") { base.content = ""; base.content2 = ""; }
  return { ...base, ...overrides };
}

// ─── Block <-> Markdown conversion ──────────────────────────────────────────

export function blocksToMarkdown(blocks: ContentBlock[]): string {
  return blocks.map((block) => {
    switch (block.type) {
      case "heading": {
        const prefix = "#".repeat(block.level || 2);
        return `${prefix} ${block.content}`;
      }
      case "image":
        return `![${block.alt || "Image"}](${block.url || ""})`;
      case "button":
        return `[${block.content}](${block.url || "#"})`;
      case "divider":
        return "---";
      case "spacer":
        return "\n";
      case "list":
        return block.content
          .split("\n")
          .filter((l) => l.trim())
          .map((l) => `- ${l.replace(/^[-*]\s*/, "")}`)
          .join("\n");
      case "numbered_list":
        return block.content
          .split("\n")
          .filter((l) => l.trim())
          .map((l, i) => `${i + 1}. ${l.replace(/^\d+\.\s*/, "")}`)
          .join("\n");
      case "quote": {
        const lines = block.content.split("\n").map((l) => `> ${l}`).join("\n");
        return block.alt ? `${lines}\n> \n> -- *${block.alt}*` : lines;
      }
      case "callout": {
        const style = block.alt || "info";
        const icon = style === "warning" ? "!!!" : style === "tip" ? ">>>" : ":::";
        return `${icon} ${style.toUpperCase()}\n${block.content}\n${icon}`;
      }
      case "video":
        return `[![${block.content}](${block.alt || ""})](${block.url || ""})`;
      case "signature":
        return `---\n${block.content}\n{{BROKER_SIGNATURE}}`;
      case "two_column":
        return `|||\n${block.content}\n|||\n${block.content2 || ""}\n|||`;
      case "text":
      default:
        return block.content;
    }
  }).join("\n\n");
}

export function markdownToBlocks(markdown: string): ContentBlock[] {
  if (!markdown.trim()) return [createBlock("text")];

  const blocks: ContentBlock[] = [];
  // Split on double newlines, but keep dividers and spacers intact
  const sections = markdown.split(/\n\n+/);

  for (let si = 0; si < sections.length; si++) {
    const section = sections[si];
    const trimmed = section.trim();
    if (!trimmed) {
      blocks.push(createBlock("spacer"));
      continue;
    }

    // Signature block: ---\ntext\n{{BROKER_SIGNATURE}} all in one section
    const sigMatch = trimmed.match(/^---+\n([\s\S]*?)(?:\n?\{\{BROKER_SIGNATURE\}\})?$/);
    if (sigMatch && sigMatch[1] && !sigMatch[1].startsWith("#") && sigMatch[1].split("\n").length <= 5) {
      const sigContent = sigMatch[1].replace(/\{\{BROKER_SIGNATURE\}\}/g, "").trim();
      if (sigContent) {
        blocks.push(createBlock("signature", { content: sigContent }));
        continue;
      }
    }

    // Divider (but not signature which is ---\ntext)
    if (/^---+$/.test(trimmed)) {
      // Check if next section looks like a signature (short text after divider)
      const nextSection = sections[si + 1]?.trim();
      if (nextSection && !nextSection.startsWith("#") && !nextSection.startsWith("!") && !nextSection.startsWith("[") && nextSection.split("\n").length <= 5 && nextSection.length < 200) {
        const sigContent = nextSection.replace(/\n?\{\{BROKER_SIGNATURE\}\}/, "").trim();
        blocks.push(createBlock("signature", { content: sigContent }));
        si++; // skip the next section
        continue;
      }
      blocks.push(createBlock("divider"));
      continue;
    }

    // Callout blocks: ::: INFO / !!! WARNING / >>> TIP
    const calloutMatch = trimmed.match(/^(:::|\!\!\!|>>>)\s*(\w+)\n([\s\S]*?)\n\1$/);
    if (calloutMatch) {
      blocks.push(createBlock("callout", {
        alt: calloutMatch[2].toLowerCase(),
        content: calloutMatch[3],
      }));
      continue;
    }

    // Two column blocks: ||| col1 ||| col2 |||
    const twoColMatch = trimmed.match(/^\|\|\|\n([\s\S]*?)\n\|\|\|\n([\s\S]*?)\n\|\|\|$/);
    if (twoColMatch) {
      blocks.push(createBlock("two_column", {
        content: twoColMatch[1],
        content2: twoColMatch[2],
      }));
      continue;
    }

    // Heading
    const headingMatch = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      blocks.push(createBlock("heading", {
        level: headingMatch[1].length as 1 | 2 | 3,
        content: headingMatch[2],
      }));
      continue;
    }

    // Image
    const imageMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imageMatch) {
      blocks.push(createBlock("image", {
        alt: imageMatch[1],
        url: imageMatch[2],
        content: "",
      }));
      continue;
    }

    // Video thumbnail: [![text](thumb)](url)
    const videoMatch = trimmed.match(/^\[!\[([^\]]*)\]\(([^)]*)\)\]\(([^)]+)\)$/);
    if (videoMatch) {
      blocks.push(createBlock("video", {
        content: videoMatch[1],
        alt: videoMatch[2],    // thumbnail
        url: videoMatch[3],    // video link
      }));
      continue;
    }

    // Quote (lines starting with >)
    const lines = trimmed.split("\n");
    if (lines.every((l) => l.startsWith(">") || l.trim() === "")) {
      const quoteLines = lines.map((l) => l.replace(/^>\s?/, ""));
      // Check for attribution: last non-empty line starting with --
      const attrIdx = findLastIdx(quoteLines, (l: string) => l.startsWith("-- *") || l.startsWith("--"));
      let attribution = "";
      let quoteContent: string;
      if (attrIdx >= 0) {
        attribution = quoteLines[attrIdx].replace(/^--\s*\*?/, "").replace(/\*$/, "").trim();
        quoteContent = quoteLines.slice(0, attrIdx).filter((l) => l.trim()).join("\n");
      } else {
        quoteContent = quoteLines.filter((l) => l.trim()).join("\n");
      }
      blocks.push(createBlock("quote", { content: quoteContent, alt: attribution }));
      continue;
    }

    // Numbered list (all lines start with digit.)
    if (lines.every((l) => /^\s*\d+\.\s+/.test(l))) {
      blocks.push(createBlock("numbered_list", {
        content: lines.map((l) => l.replace(/^\s*\d+\.\s+/, "")).join("\n"),
      }));
      continue;
    }

    // Bullet list (all lines start with - or *)
    if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
      blocks.push(createBlock("list", {
        content: lines.map((l) => l.replace(/^\s*[-*]\s+/, "")).join("\n"),
      }));
      continue;
    }

    // Standalone link that looks like a button (single line, just a link)
    const buttonMatch = trimmed.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (buttonMatch && !trimmed.includes("\n")) {
      blocks.push(createBlock("button", {
        content: buttonMatch[1],
        url: buttonMatch[2],
      }));
      continue;
    }

    // Default: text block
    blocks.push(createBlock("text", { content: trimmed }));
  }

  return blocks.length > 0 ? blocks : [createBlock("text")];
}

// ─── Markdown to HTML for preview ───────────────────────────────────────────

export const BROKER_SIGNATURE_HTML = `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-top: 8px;"><tr><td style="vertical-align: top;"><img src="${EMAIL_BRAND.siteUrl}/people/Cajetan%20Photo.JPG" alt="Cajetan Silva" width="56" height="56" style="display: block; width: 56px; height: 56px; border-radius: 50%; object-fit: cover;" /></td><td style="padding-left: 14px; vertical-align: middle;"><p style="margin: 0; font-size: 15px; font-weight: 700; color: ${EMAIL_BRAND.primary};">Cajetan Silva</p><p style="margin: 2px 0 0 0; font-size: 13px; color: ${EMAIL_BRAND.textLight};">Principal Broker</p><p style="margin: 2px 0 0 0; font-size: 13px;"><a href="tel:0477308028" style="color: ${EMAIL_BRAND.accent}; text-decoration: none;">0477 308 028</a></p><p style="margin: 2px 0 0 0; font-size: 13px;"><a href="mailto:cjsilva@pathfinancial.com.au" style="color: ${EMAIL_BRAND.accent}; text-decoration: none;">cjsilva@pathfinancial.com.au</a></p></td></tr></table>`;

export function contentToHtml(content: string): string {
  // Pre-merge: collapse blank lines between consecutive bullet/numbered list items
  let pre = content;
  // Keep collapsing until stable (handles 3+ items with gaps)
  let prev = "";
  while (prev !== pre) {
    prev = pre;
    pre = pre.replace(/(^\s*[-*]\s+.*)\n\s*\n(\s*[-*]\s+)/gm, "$1\n$2");
    pre = pre.replace(/(^\s*\d+\.\s+.*)\n\s*\n(\s*\d+\.\s+)/gm, "$1\n$2");
  }
  return pre
    // Broker signature card
    .replace(/\{\{BROKER_SIGNATURE\}\}/g, BROKER_SIGNATURE_HTML)
    // Callout blocks
    .replace(
      /^(:::|\!\!\!|>>>)\s*(\w+)\n([\s\S]*?)\n\1$/gim,
      (_match, _marker, style, text) => {
        const colors: Record<string, { bg: string; border: string; icon: string }> = {
          info: { bg: "#eff6ff", border: "#3b82f6", icon: "&#8505;" },
          warning: { bg: "#fffbeb", border: "#f59e0b", icon: "&#9888;" },
          tip: { bg: "#f0fdf4", border: "#22c55e", icon: "&#10004;" },
        };
        const c = colors[style.toLowerCase()] || colors.info;
        return `<div style="border-left: 4px solid ${c.border}; background: ${c.bg}; padding: 16px 20px; border-radius: 0 8px 8px 0; margin: 16px 0;"><span style="font-weight: 600; text-transform: uppercase; font-size: 12px; letter-spacing: 0.5px; color: ${c.border};">${c.icon} ${style.toUpperCase()}</span><br>${text}</div>`;
      }
    )
    // Two column blocks
    .replace(
      /^\|\|\|\n([\s\S]*?)\n\|\|\|\n([\s\S]*?)\n\|\|\|$/gim,
      (_match, col1, col2) => `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin: 16px 0;"><tr><td style="width: 48%; vertical-align: top; padding-right: 12px; color: ${EMAIL_BRAND.text}; font-size: 16px; line-height: 1.6;">${col1}</td><td style="width: 4%;"></td><td style="width: 48%; vertical-align: top; padding-left: 12px; color: ${EMAIL_BRAND.text}; font-size: 16px; line-height: 1.6;">${col2}</td></tr></table>`
    )
    // Quote blocks
    .replace(
      /^(?:>\s?(.*)(?:\n|$))+/gim,
      (match) => {
        const lines = match.split("\n").map(l => l.replace(/^>\s?/, "")).filter(l => l.trim());
        const attrIdx = findLastIdx(lines, (l: string) => l.startsWith("-- "));
        let quote: string, attr = "";
        if (attrIdx >= 0) {
          attr = lines[attrIdx].replace(/^--\s*\*?/, "").replace(/\*$/, "");
          quote = lines.slice(0, attrIdx).join("<br>");
        } else {
          quote = lines.join("<br>");
        }
        return `<div style="border-left: 4px solid ${EMAIL_BRAND.accent}; padding: 16px 20px; margin: 16px 0; background: #f8fafc; border-radius: 0 8px 8px 0; font-style: italic; color: ${EMAIL_BRAND.text};">${quote}${attr ? `<br><span style="font-style: normal; font-weight: 600; color: ${EMAIL_BRAND.primary}; margin-top: 8px; display: inline-block;">-- ${attr}</span>` : ""}</div>`;
      }
    )
    // Video thumbnails: [![text](thumb)](url)
    .replace(
      /\[!\[([^\]]*)\]\(([^)]*)\)\]\(([^)]+)\)/g,
      (_match, text, thumb, url) => {
        if (thumb) {
          return `<a href="${url}" style="display: block; text-decoration: none; margin: 16px 0;"><div style="position: relative; text-align: center;"><img src="${thumb}" alt="${text}" style="max-width: 100%; height: auto; border-radius: 8px;" /><div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 64px; height: 64px; background: rgba(0,0,0,0.7); border-radius: 50%; display: flex; align-items: center; justify-content: center;"><span style="font-size: 28px; color: white; margin-left: 4px;">&#9654;</span></div></div></a>`;
        }
        return `<a href="${url}" style="display: inline-block; padding: 12px 24px; background: ${EMAIL_BRAND.primary}; color: white; text-decoration: none; border-radius: 6px; font-weight: 500; margin: 16px 0;">&#9654; ${text}</a>`;
      }
    )
    // Headers
    .replace(/^### (.*$)/gim, `<h3 style="font-size: 18px; font-weight: 600; color: ${EMAIL_BRAND.primary}; margin: 24px 0 12px 0; font-family: Georgia, 'Times New Roman', serif;">$1</h3>`)
    .replace(/^## (.*$)/gim, `<h2 style="font-size: 22px; font-weight: 600; color: ${EMAIL_BRAND.primary}; margin: 28px 0 14px 0; font-family: Georgia, 'Times New Roman', serif;">$1</h2>`)
    .replace(/^# (.*$)/gim, `<h1 style="font-size: 26px; font-weight: 700; color: ${EMAIL_BRAND.primary}; margin: 32px 0 16px 0; font-family: Georgia, 'Times New Roman', serif;">$1</h1>`)
    // Images
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, `<img src="$2" alt="$1" style="max-width: 100%; height: auto; display: block; border-radius: 8px; margin: 16px 0;" />`)
    // Dividers
    .replace(/^---$/gim, `<hr style="border: none; border-top: 1px solid #e2e8f0; margin: 24px 0;" />`)
    // Bold and italic
    .replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, `<a href="$2" style="color: ${EMAIL_BRAND.accent}; text-decoration: underline; font-weight: 500;">$1</a>`)
    // Numbered lists
    .replace(/^\s*(\d+)\.\s+(.*)$/gim, `<tr><td style="padding: 4px 0 4px 20px; color: ${EMAIL_BRAND.text};">$1. $2</td></tr>`)
    .replace(
      /(<tr><td style="padding: 4px 0 4px 20px;[^>]*>\d+\.[\s\S]*?<\/td><\/tr>\n?)+/g,
      (match) => `<table role="presentation" style="margin: 16px 0;" cellpadding="0" cellspacing="0" border="0" width="100%">${match.replace(/\n/g, "")}</table>`
    )
    // Unordered lists
    .replace(/^\s*[-*]\s+(.*)$/gim, `<tr><td style="padding: 6px 0 6px 20px; color: ${EMAIL_BRAND.text}; font-size: 16px; line-height: 1.6;">&#8226; $1</td></tr>`)
    .replace(
      /(<tr><td style="padding: 6px 0 6px 20px;[^>]*>&#8226;[\s\S]*?<\/td><\/tr>\n?)+/g,
      (match) => `<table role="presentation" style="margin: 16px 0;" cellpadding="0" cellspacing="0" border="0" width="100%">${match.replace(/\n/g, "")}</table>`
    )
    // Single newlines to breaks (before paragraph splitting)
    .replace(/\n/g, "<br>")
    // Split on double-break boundaries into segments, wrap text-only segments in <p> tags
    .split("<br><br>")
    .map((segment) => {
      const trimmed = segment.replace(/^(<br>)+|(<br>)+$/g, "").trim();
      if (!trimmed) return "";
      // Don't wrap block-level elements in <p>
      if (/^<(table|hr|div|img|h[1-3]|a\s[^>]*style="[^"]*display:\s*(?:inline-)?block)/i.test(trimmed)) {
        return trimmed;
      }
      const isBullet = /^(&#8226;|•|\*\s|[-]\s)/.test(trimmed) || /^<tr><td/.test(trimmed);
      const margin = isBullet ? "0 0 4px 0" : "0 0 16px 0";
      return `<p style="margin: ${margin}; color: ${EMAIL_BRAND.text}; font-size: 16px; line-height: 1.6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">${trimmed}</p>`;
    })
    .filter(Boolean)
    .join("\n");
}

export function interpolatePreview(template: string): string {
  const sampleData: Record<string, string> = {
    "{{name}}": "John Smith",
    "{{firstName}}": "John",
    "{{preferredName}}": "Johnny",
    "{{email}}": "john@example.com",
    "{{loanType}}": "home purchase",
    "{{years}}": "3",
    "{{allLoans}}": `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin:12px 0;"><tr style="background:#00263e;"><th style="padding:10px 14px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#ffffff;text-align:left;">Purpose</th><th style="padding:10px 14px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#ffffff;text-align:left;">Lender</th><th style="padding:10px 14px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#ffffff;text-align:right;">Amount</th><th style="padding:10px 14px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#ffffff;text-align:right;">Settled</th></tr><tr style="background:#ffffff;"><td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;font-size:14px;">Refinance</td><td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;font-size:14px;">ANZ</td><td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;font-size:14px;text-align:right;font-weight:600;">$450,000</td><td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;font-size:14px;text-align:right;">Mar 2022</td></tr><tr style="background:#f8fafc;"><td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;font-size:14px;">Home Purchase</td><td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;font-size:14px;">Commonwealth Bank</td><td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;font-size:14px;text-align:right;font-weight:600;">$620,000</td><td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;font-size:14px;text-align:right;">Jun 2020</td></tr></table>`,
    "{{loanCount}}": "2",
  };
  let result = template;
  for (const [key, value] of Object.entries(sampleData)) {
    result = result.replaceAll(key, value);
  }
  return result;
}

/**
 * Wraps HTML body content in the full branded email layout for preview.
 * Mirrors the backend wrapEmail() from layout.ts.
 */
export function wrapEmailPreview(body: string, _subject?: string): string {
  const B = EMAIL_BRAND;
  const year = new Date().getFullYear();
  return `
<div style="background-color:${B.cream};padding:20px 10px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:600px;margin:0 auto;background-color:${B.white};border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.05);">
    <tr>
      <td align="center" style="padding:28px 40px 20px 40px;background-color:${B.white};">
        <a href="${B.siteUrl}" style="text-decoration:none;">
          <img src="${B.logoUrl}" alt="${B.companyName}" width="220" style="display:block;width:220px;max-width:100%;height:auto;" />
        </a>
      </td>
    </tr>
    <tr>
      <td style="padding:0 40px;">
        <div style="border-top:3px solid ${B.primary};"></div>
      </td>
    </tr>
    <tr>
      <td style="padding:32px 40px 40px 40px;">
        ${body}
      </td>
    </tr>
    <tr>
      <td style="padding:0 40px;">
        <div style="border-top:1px solid ${B.border};"></div>
      </td>
    </tr>
    <tr>
      <td style="padding:28px 40px;background-color:${B.cream};text-align:center;font-size:13px;color:${B.textLight};line-height:1.6;">
        <p style="margin:0 0 6px 0;"><strong style="color:${B.primary};">${B.companyName}</strong></p>
        <p style="margin:0 0 6px 0;">Australian Credit Licence ${B.acl} | ABN ${B.abn}</p>
        <p style="margin:0 0 12px 0;">${B.address}</p>
        <p style="margin:0;">
          <a href="#" style="color:${B.accent};text-decoration:underline;">Manage Preferences</a>
          &nbsp;&nbsp;|&nbsp;&nbsp;
          <a href="#" style="color:${B.accent};text-decoration:underline;">Unsubscribe</a>
        </p>
      </td>
    </tr>
    <tr>
      <td align="center" style="padding:12px 40px 20px 40px;">
        <p style="margin:0;font-size:11px;color:${B.textLight};">&copy; ${year} ${B.companyName}. All rights reserved.</p>
        <p style="margin:4px 0 0 0;font-size:11px;">
          <a href="${B.siteUrl}" style="color:${B.textLight};text-decoration:underline;">pathfinancial.com.au</a>
        </p>
      </td>
    </tr>
  </table>
</div>`.trim();
}
